import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'

import type { ICockpitCompletenessData } from '../model/types'

export interface ICockpitPlanningStarted {
  /** Keyed by the offering as `completenessData` names it, trimmed. */
  byOffering: ReadonlyMap<string, boolean>
  overall: boolean
}

const normalizeId = (value: string) => value.trim().toLowerCase()

/** Whether planning has started (`IsPlanned`) for each offering of the given partners: an
 *  offering counts once any of them has planned it, `overall` once any offering has. An offering
 *  with no completeness entry has not started. No longer gates Plan Quality: `IsPlanned` came in
 *  as `0` for every partner — see the README's Plan Quality note. */
export const getCockpitPlanningStarted = (
  completenessData: ICockpitCompletenessData[],
  partnerIds: string[]
): ICockpitPlanningStarted => {
  const partners = new Set(partnerIds.map(normalizeId))
  const byOffering = new Map<string, boolean>()

  completenessData.forEach(({ PartnerId, Offering, CompletenessData }) => {
    if (!partners.has(normalizeId(PartnerId))) {
      return
    }

    const offering = Offering.trim()
    const isPlanned = CompletenessData.some(({ IsPlanned }) => IsPlanned === 1)

    byOffering.set(offering, Boolean(byOffering.get(offering)) || isPlanned)
  })

  return {
    byOffering,
    overall: Array.from(byOffering.values()).some(Boolean),
  }
}

export const isCockpitPlanningStarted = (
  { byOffering, overall }: ICockpitPlanningStarted,
  offering: string
) =>
  offering === ProgramTypes.Overall || offering === ProgramTypes.Consolidated
    ? overall
    : (byOffering.get(offering.trim()) ?? false)
